export const MM_PER_POINT = 25.4 / 72;

export function dimensions(format) {
    const box = format.geometry.print_box;
    return { width: box.x2 - box.x1, height: box.y2 - box.y1 };
}

export function pixelSize(size, dpi = 300) {
    return { width: Math.round((size.width * dpi) / 25.4), height: Math.round((size.height * dpi) / 25.4) };
}

export function constrain(object, size) {
    const result = { ...object };
    if (object.type === 'image') return result;
    result.x = Math.max(0, Math.min(size.width - result.width, result.x));
    result.y = Math.max(0, Math.min(size.height - result.height, result.y));
    return result;
}

// Threshold is supplied in document millimetres, independent of display zoom.
export function snapPosition(object, size, threshold) {
    const result = constrain(object, size);
    const guides = [];
    [['x', 'width'], ['y', 'height']].forEach(([axis, length]) => {
        const center = (size[length] - result[length]) / 2;
        if (Math.abs(result[axis] - center) <= threshold) {
            result[axis] = center;
            guides.push(axis);
        }
    });
    return { object: result, guides };
}

export function fitImage(width, height, size) {
    const scale = Math.min(size.width / width, size.height / height);
    return { width: width * scale, height: height * scale };
}

export function layoutProblems(layout, size, images) {
    const errors = [];
    const warnings = [];
    layout.objects.forEach((object) => {
        if (![object.x, object.y, object.width, object.height].every(Number.isFinite)
            || object.width <= 0 || object.height <= 0
            || (object.type !== 'image' && (object.x < 0 || object.y < 0
                || object.x + object.width > size.width + 0.02 || object.y + object.height > size.height + 0.02))) {
            errors.push('Ett objekt ryms inte inom tryckytan. Minska storleken eller flytta objektet.');
        }
        if (object.type === 'text' && (!Number.isFinite(object.font_size) || object.font_size < 4 || object.font_size > 150
            || !Number.isFinite(object.line_height) || object.line_height < 0.8 || object.line_height > 3)) {
            errors.push('Textstorleken måste vara 4–150 pt och radavståndet 0,8–3.');
        }
        if (object.type === 'image') {
            const image = images.get(object.image_id);
            if (!image) {
                errors.push(`Bild ${object.image_id} kunde inte laddas.`);
            } else {
                const dpi = Math.min(image.naturalWidth / object.width, image.naturalHeight / object.height) * 25.4;
                if (dpi < 150 - 1e-6) errors.push(`Bild ${object.image_id} måste ha minst 150 dpi. Minska bildens storlek.`);
                else if (dpi < 300) warnings.push(`Bild ${object.image_id} har lägre upplösning än 300 dpi vid vald storlek.`);
            }
        }
    });
    return { errors: [...new Set(errors)], warnings: [...new Set(warnings)] };
}

export function designDimensions(layout, formats) {
    const geometry = layout.format.startsWith('series:') ? layout.format_geometry : formats.find((format) => format.id === layout.format)?.geometry;
    if (!geometry) throw new Error('Designens sparade tryckformat saknas.');
    return dimensions({ geometry });
}

// Compose the active order greeting: a whole image, or text with an optional logo.
export function orderGreetingObjects(greeting, size, fonts, images, measureText) {
    const source = (value) => {
        const id = Number(value);
        if (!(id > 0)) return null;
        const image = images.get(id);
        if (!image) throw new Error(`Bild ${id} kunde inte laddas från mediabanken.`);
        return { ...image, id };
    };
    const image = source(greeting.GreetingImageID);
    if (image) {
        const frame = /^greeting-design:v2:([1-9][0-9]*)x([1-9][0-9]*)$/.exec(greeting.GreetingImageNote || '');
        const hasFrame = frame && image.width <= Number(frame[1]) && image.height <= Number(frame[2]);
        const scale = hasFrame ? Math.min(size.width / Number(frame[1]), size.height / Number(frame[2])) : Math.min(size.width / image.width, size.height / image.height, 1);
        const width = image.width * scale;
        const height = image.height * scale;
        return [{
            id: 'order-image', type: 'image', image_id: image.id, x: (size.width - width) / 2, y: (size.height - height) / 2, width, height,
        }];
    }
    const logo = source(greeting.GreetingLogoID);
    const gap = 7;
    const object = {
        id: 'order-text',
        type: 'text',
        text: String(greeting.GreetingText || ''),
        font: fonts.some((font) => font.id === greeting.GreetingFont) ? greeting.GreetingFont : 'arial',
        color: /^#[a-f0-9]{6}$/i.test(greeting.GreetingColor) ? greeting.GreetingColor : '#000000',
        font_size: Math.max(4, Math.min(150, (Number(greeting.GreetingSize) || 28) * 0.75)),
        line_height: 1.2,
        align: 'center',
        x: size.width * 0.1,
        y: 0,
        width: size.width * 0.8,
        height: 1,
    };
    let textLimit = size.height;
    if (logo) textLimit -= gap * 3 + 4;
    object.height = measureText(object);
    while (object.height > textLimit && object.font_size > 4) {
        object.font_size = Math.max(4, object.font_size - 0.5);
        object.height = measureText(object);
    }
    if (object.height > textLimit) throw new Error('Orderns hälsning är för lång för standardmallen i valt tryckformat. Välj en större tryckyta eller korta texten på ordern.');
    const availableHeight = size.height - object.height;
    const logoHeight = logo ? Math.min(availableHeight - gap * 3, logo.height, 15) : 0;
    const marginTop = logo ? Math.max(0, (availableHeight - logoHeight - gap) / 2) : gap;
    const objects = [];
    const addImage = (id, bitmap, height, y) => {
        if (!bitmap || height <= 0) return;
        // insertImage fits within the full width, centers in its slot and never upscales.
        const scale = Math.min(size.width / bitmap.width, height / bitmap.height, 1);
        const width = bitmap.width * scale;
        const drawHeight = bitmap.height * scale;
        objects.push({
            id, type: 'image', image_id: bitmap.id, width, height: drawHeight, x: (size.width - width) / 2, y: y + (height - drawHeight) / 2,
        });
    };
    // Keep text-only and logo-only compositions centered with a gap between objects.
    object.y = (size.height - object.height) / 2;
    if (logo) object.y = marginTop;
    objects.push(object);
    addImage('order-logo', logo, logoHeight, object.y + object.height + gap);
    return objects;
}

// Remove equal pixel insets on opposing sides, preserving the original canvas center.
export function publicationCrop(layout, size) {
    const pixels = pixelSize(size);
    const objects = layout.objects.filter((object) => object.type !== 'text' || object.text.replace(/[ \t\n\r]/g, '') !== '');
    let insetX = size.width / 2;
    let insetY = size.height / 2;
    objects.forEach((object) => {
        insetX = Math.min(insetX, object.x - 10, size.width - object.x - object.width - 10);
        insetY = Math.min(insetY, object.y - 10, size.height - object.y - object.height - 10);
    });
    const x = objects.length ? Math.max(0, Math.floor((insetX * pixels.width) / size.width)) : 0;
    const y = objects.length ? Math.max(0, Math.floor((insetY * pixels.height) / size.height)) : 0;
    return {
        x, y, width: pixels.width - 2 * x, height: pixels.height - 2 * y, full_width: pixels.width, full_height: pixels.height,
    };
}
